import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useEffect,
} from "react";
import { useLocation } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import { grey } from "@mui/material/colors";

import CustomInput from "components/CustomInput/CustomInput.js";
import AccountApi from "../../../api_calls/AccountApi";

// --- Patched CustomDropdown with grey buttons ---
const CustomDropdown = ({ buttonText, dropdownList, onClick, buttonProps }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (value) => {
    setAnchorEl(null);
    if (value !== undefined) {
      onClick(value);
    }
  };

  return (
    <>
      <Button
        {...buttonProps}
        onClick={handleClick}
        variant="outlined"
        sx={{
          borderColor: grey[700],
          color: grey[700],
          "&:hover": {
            borderColor: grey[800],
            backgroundColor: grey[100],
          },
        }}
      >
        {buttonText}
      </Button>
      <Menu anchorEl={anchorEl} open={open} onClose={() => handleClose()}>
        {dropdownList.map((item, index) => (
          <MenuItem key={index} onClick={() => handleClose(item)}>
            {item}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

// --- Step1 Component ---
const Step1 = forwardRef((props, ref) => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const referralCodeFromURL = query.get("ref") || "";

  const [firstName, setFirstName] = useState("");
  const [otherNames, setOtherNames] = useState("");
  const [referralCode, setReferralCode] = useState(referralCodeFromURL); // ✅ initialize with URL param
  const [nickname, setNickname] = useState("");
  const [dobDay, setDobDay] = useState("Day");
  const [dobMonth, setDobMonth] = useState("Month");
  const [dobYear, setDobYear] = useState("Year");
  const [errors, setErrors] = useState({});

  // ✅ Upline name fetch state
  const [uplineName, setUplineName] = useState("");
  const [uplineStatus, setUplineStatus] = useState("idle"); // idle | loading | success | fail

  // --- Years with 18+ restriction ---
  const currentYear = new Date().getFullYear();
  const maxYear = currentYear - 18;
  const minYear = currentYear - 100;
  const years = Array.from({ length: maxYear - minYear + 1 }, (_, i) =>
    (maxYear - i).toString(),
  );

  // ✅ Fetch upline name when referral code changes (debounced)
  useEffect(() => {
    const refCode = (referralCode || "").trim();

    // reset when empty
    if (!refCode) {
      setUplineName("");
      setUplineStatus("idle");
      return;
    }

    setUplineStatus("loading");

    const timer = setTimeout(async () => {
      try {
        const res = await AccountApi.getUserNameByAccount(refCode);

        if (res?.status === "success" && res?.data?.full_name) {
          setUplineName(res.data.full_name);
          setUplineStatus("success");
        } else {
          setUplineName("");
          setUplineStatus("fail");
        }
      } catch (e) {
        setUplineName("");
        setUplineStatus("fail");
      }
    }, 450);

    return () => clearTimeout(timer);
  }, [referralCode]);

  const validateFields = () => {
    const newErrors = {};
    if (!firstName.trim()) newErrors.firstName = "First name is required";
    if (!otherNames.trim()) newErrors.otherNames = "Other names are required";

    if (!nickname.trim()) {
      newErrors.nickname = "Nickname is required";
    } else if (/\s/.test(nickname)) {
      newErrors.nickname = "Nickname cannot contain spaces";
    } else if (nickname.length > 15) {
      newErrors.nickname = "Nickname cannot exceed 15 characters";
    }

    if (dobDay === "Day" || dobMonth === "Month" || dobYear === "Year") {
      newErrors.dob = "Please select a valid date of birth";
    }

    // ✅ Optional: if referral code is provided, it must be valid
    if (referralCode.trim() && uplineStatus === "fail") {
      newErrors.referralCode = "Invalid referral code";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useImperativeHandle(ref, () => ({
    sendState: () => {
      if (validateFields()) {
        return {
          firstName,
          otherNames,
          referralCode,
          nickname,
          dobDay,
          dobMonth,
          dobYear,
        };
      }
      return null;
    },
    validateBeforeNext: validateFields,
  }));

  return (
    <Grid container justifyContent="center" spacing={2}>
      <Grid item xs={12}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 300, my: 2, textAlign: "center" }}
        >
          Let's start with your personal details
        </Typography>
      </Grid>

      {/* First Name */}
      <Grid item xs={12} sm={6}>
        <CustomInput
          labelText="First Name (required)"
          id="first-name"
          error={Boolean(errors.firstName)}
          formControlProps={{ fullWidth: true }}
          inputProps={{
            value: firstName,
            onChange: (event) => setFirstName(event.target.value),
          }}
        />
        {errors.firstName && (
          <Box sx={{ color: "red", fontSize: "0.8rem", mt: 1 }}>
            {errors.firstName}
          </Box>
        )}
      </Grid>

      {/* Other Names */}
      <Grid item xs={12} sm={6}>
        <CustomInput
          labelText="Other Names (required)"
          id="other-names"
          error={Boolean(errors.otherNames)}
          formControlProps={{ fullWidth: true }}
          inputProps={{
            value: otherNames,
            onChange: (event) => setOtherNames(event.target.value),
          }}
        />
        {errors.otherNames && (
          <Box sx={{ color: "red", fontSize: "0.8rem", mt: 1 }}>
            {errors.otherNames}
          </Box>
        )}
      </Grid>

      {/* Referral Code */}
      {/* Referral Code */}
      <Grid item xs={12} sm={6}>
        <CustomInput
          labelText="Referral Code (Optional)"
          id="referral-code"
          error={Boolean(errors.referralCode)}
          formControlProps={{
            fullWidth: true,
            sx: { mb: 0 }, // ✅ remove bottom margin from CustomInput
          }}
          inputProps={{
            value: referralCode,
            onChange: (event) => setReferralCode(event.target.value),
          }}
        />

        {/* ✅ Referrer name / status — tight under input */}
        <Box sx={{ mt: "-4px", minHeight: "18px" }}>
          {uplineStatus === "loading" && (
            <Typography sx={{ color: grey[600], fontSize: "0.8rem" }}>
              Loading referrer name...
            </Typography>
          )}

          {uplineStatus === "success" && (
            <Typography sx={{ color: "green", fontSize: "0.85rem" }}>
              Referred by: {uplineName}
            </Typography>
          )}

          {uplineStatus === "fail" && referralCode.trim() && (
            <Typography sx={{ color: "red", fontSize: "0.8rem" }}>
              Invalid referral code
            </Typography>
          )}
        </Box>

        {errors.referralCode && (
          <Typography sx={{ color: "red", fontSize: "0.8rem", mt: 0.5 }}>
            {errors.referralCode}
          </Typography>
        )}
      </Grid>

      {/* Nickname */}
      <Grid item xs={12} sm={6}>
        <CustomInput
          labelText="Nickname (required, no spaces)"
          id="pseudo-name"
          error={Boolean(errors.nickname)}
          formControlProps={{ fullWidth: true }}
          inputProps={{
            value: nickname,
            maxLength: 15,
            onChange: (event) => {
              const value = event.target.value;
              if (/\s/.test(value)) {
                setErrors((prev) => ({
                  ...prev,
                  nickname: "Nickname cannot contain spaces",
                }));
                return;
              }
              setNickname(value);
              if (!value.trim()) {
                setErrors((prev) => ({
                  ...prev,
                  nickname: "Nickname is required",
                }));
              } else {
                setErrors((prev) => {
                  const { nickname, ...rest } = prev;
                  return rest;
                });
              }
            },
            onKeyDown: (event) => {
              if (event.key === " ") {
                event.preventDefault();
                setErrors((prev) => ({
                  ...prev,
                  nickname: "Nickname cannot contain spaces",
                }));
              }
            },
          }}
        />
        {errors.nickname && (
          <Box sx={{ color: "red", fontSize: "0.8rem", mt: 1 }}>
            {errors.nickname}
          </Box>
        )}
      </Grid>

      {/* Date of Birth */}
      <Grid item xs={12}>
        <Typography>Date of Birth (Required)</Typography>
      </Grid>

      <Grid item xs={12} sm={4}>
        <CustomDropdown
          buttonText={dobDay}
          dropdownList={[...Array(31).keys()].map((d) => (d + 1).toString())}
          onClick={(value) => setDobDay(value)}
          buttonProps={{ fullWidth: true }}
        />
      </Grid>

      <Grid item xs={12} sm={4}>
        <CustomDropdown
          buttonText={dobMonth}
          dropdownList={[
            "01",
            "02",
            "03",
            "04",
            "05",
            "06",
            "07",
            "08",
            "09",
            "10",
            "11",
            "12",
          ]}
          onClick={(value) => setDobMonth(value)}
          buttonProps={{ fullWidth: true }}
        />
      </Grid>

      <Grid item xs={12} sm={4}>
        <CustomDropdown
          buttonText={dobYear}
          dropdownList={years}
          onClick={(value) => setDobYear(value)}
          buttonProps={{ fullWidth: true }}
        />
      </Grid>

      {errors.dob && (
        <Grid item xs={12}>
          <Box sx={{ color: "red", fontSize: "0.8rem", mt: 1 }}>
            {errors.dob}
          </Box>
        </Grid>
      )}
    </Grid>
  );
});

Step1.displayName = "Step1";

export default Step1;
